import {
  Registration,
  RegistrationServiceClient,
  VerifyRegistrationRequest,
  RegistrationAccount,
  RegistrationOwner,
  RegistrationCorpPrimaryAgentAddress,
  SaveOnboardingPercentRequest,
  RegistrationIdentification,
  SaveRegistrationIdentificationRequest,
  RegistrationTrustedContact,
  RegistrationEmployment,
  RegistrationAssociation,
  SaveRegistrationDisclosureRequest,
  SaveRegistrationTradingRequest,
  RegistrationObjective,
  SignRegistrationRequest,
  GetRegistrationAccountRequest,
  SaveRegistrationKycRequest,
} from '../proto/usrpb/registration_grpc_web_pb';
import { SetTokens } from './AuthService';
import { auth } from '../lib/auth/Auth';
import { stringToProtoDate } from './ConvertService';

const serviceNoAuth = new RegistrationServiceClient(window.env.GRPC_ENDPOINT);
const service = new RegistrationServiceClient(window.env.GRPC_ENDPOINT, {}, { ...auth });

const requestParameter = (param) => {
  let req = new Registration();
  req.setVerificationKey(param.verificationKey);
  req.setFirstName(param.firstName);
  req.setMiddleName(param.middleName);
  req.setLastName(param.lastName);
  req.setSuffix(param.suffix);
  req.setEmail(param.email);
  req.setPassword(param.password);
  req.setSecurityWord(param.securityWord);
  req.setSecurityWordHint(param.securityWordHint);
  req.setPromoCode(param.promoCode);
  req.setPhoneNumber(param.phoneNumber);

  return req;
};

export async function createRegistration(param) {
  return new Promise((resolve, reject) => {
    const req = requestParameter(param);

    serviceNoAuth.createRegistration(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function verifyRegistration(verificationKey) {
  return new Promise((resolve, reject) => {
    let req = new VerifyRegistrationRequest();
    req.setVerificationKey(verificationKey);

    serviceNoAuth.verifyRegistration(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        const { accessToken, refreshToken, account } = response.toObject();
        SetTokens(accessToken, refreshToken, []);

        resolve(account);
      }
    });
  });
}

export async function saveOnboardingPercent(onboardingPercent) {
  return new Promise((resolve, reject) => {
    let req = new SaveOnboardingPercentRequest();
    req.setOnboardingPercent(onboardingPercent);

    service.saveOnboardingPercent(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function saveRegistrationAccount(data) {
  return new Promise((resolve, reject) => {
    let req = new RegistrationAccount();
    req.setAccountType(data.accountType);
    req.setSubType(data.subType);
    req.setAccountName(data.accountName);
    req.setTin(data.tin?.replaceAll('_',''));
    req.setTinId(data.tinId);
    req.setFormationCountry(data.formationCountry);
    req.setOnboardingPercent((data.onboardingPercent || 0) + ''); // set to string
    req.setRep(data.rep);

    const primaryOwner = newRegistrationOwner(data.primaryOwner);
    req.setPrimaryOwner(primaryOwner);

    const jointOwner = newRegistrationOwner(data.jointOwner);
    req.setJointOwner(jointOwner);

    // corporation secondary owners
    let corpOwnersList = [];
    for (let index = 0; index < data.corpOwnersList.length; index++) {
      const coOwner = newRegistrationOwner(data.corpOwnersList[index]);
      corpOwnersList.push(coOwner);
    }
    req.setCorpOwnersList(corpOwnersList);

    // corporation secondary address
    const corpPrimaryAgentAddress = newCorpPrimaryAgentAddress(
      data.corpPrimaryAgentAddress
    );
    req.setCorpPrimaryAgentAddress(corpPrimaryAgentAddress);

    service.saveRegistrationAccount(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

const newCorpPrimaryAgentAddress = (addr) => {
  const req = new RegistrationCorpPrimaryAgentAddress();
  req.setAddressId(addr.addressId);
  req.setAddress1(addr.address1);
  req.setAddress2(addr.address2);
  req.setCity(addr.city);
  req.setState(addr.state);
  req.setCountry(addr.country);
  req.setZip(addr.zip?.replaceAll('_',''));

  return req;
};

const newRegistrationOwner = (owner) => {
  let req = new RegistrationOwner();
  req.setOwnerId(owner.ownerId);
  req.setIsOfficer(owner.isOfficer);
  req.setIsOwner(owner.isOwner);
  req.setOwnershipPercent((owner.ownershipPercent || 0) + ''); // convert to string
  req.setFirstName(owner.firstName);
  req.setMiddleName(owner.middleName);
  req.setLastName(owner.lastName);

  req.setPhoneId(owner.phoneId);
  req.setPhone(owner.phone);
  req.setPhone(owner.phone);

  req.setEmailId(owner.emailId);
  req.setEmail(owner.email);

  req.setSecondaryPhoneId(owner.secondaryPhoneId);
  req.setSecondaryPhone(owner.secondaryPhone);
  req.setSecondaryPhone(owner.secondaryPhone);

  req.setAddressId(owner.addressId);
  req.setAddress1(owner.address1);
  req.setAddress2(owner.address2);
  req.setCity(owner.city);
  req.setState(owner.state);
  req.setCountry(owner.country);
  req.setZip(owner.zip?.replaceAll('_',''));
  req.setImgProofOfAddress(owner.imgProofOfAddress);

  req.setFirstName(owner.firstName);
  req.setLastName(owner.lastName);
  req.setMiddleName(owner.middleName);

  const identification = newRegistrationIdentification(owner.identification);
  req.setIdentification(identification);

  return req;
};

const newRegistrationIdentification = (data) => {
  let req = new RegistrationIdentification();
  if (!data) {
    return req;
  }

  req.setOwnerId(data.ownerId);
  req.setIdentificationId(data.identificationId);
  req.setIdentificationNo(data.identificationNo);
  req.setIdentificationType(data.identificationType);
  req.setIssueDate(stringToProtoDate(data.issueDate));
  req.setExpirationDate(stringToProtoDate(data.expirationDate));
  req.setDateOfBirth(stringToProtoDate(data.dateOfBirth));
  req.setCountryOfIssuance(data.countryOfIssuance);
  req.setImgGovernmentId(data.imgGovernmentId);
  req.setKycStatus(data.kycStatus);
  req.setStatusReason(data.statusReason);
  req.setTaxIdentificationId(data.taxIdentificationId);
  req.setTaxState(data.taxState);
  req.setTaxNo(data.taxNo);
  req.setTaxCountry(data.taxCountry);
  req.setCitizenship(data.citizenship);

  return req;
};

export async function saveRegistrationIdentification(data) {
  return new Promise((resolve, reject) => {
    const req = new SaveRegistrationIdentificationRequest();
    req.setPrimaryOwner(newRegistrationIdentification(data.primaryOwner));

    // corporation secondary owners
    let corpOwnersList = [];
    for (let index = 0; index < data.corpOwnersList.length; index++) {
      const coOwner = newRegistrationIdentification(data.corpOwnersList[index]);
      corpOwnersList.push(coOwner);
    }
    req.setCorpOwnersList(corpOwnersList);

    service.saveRegistrationIdentification(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function saveTrustedContact(data) {
  return new Promise((resolve, reject) => {
    const req = new RegistrationTrustedContact();

    req.setOwnerId(data.ownerId);

    if (data.haveTrustedContact === 'Yes') {
      req.setFirstName(data.firstName);
      req.setMiddleName(data.middleName);
      req.setLastName(data.lastName);
      req.setDateOfBirth(stringToProtoDate(data.dateOfBirth));
      req.setRelationship(data.relationship);
      req.setEmailId(data.emailId);
      req.setEmailType(data.emailType);
      req.setEmail(data.email);
      req.setPhoneId(data.phoneId);
      req.setPhoneType(data.phoneType);
      req.setPhone(data.phone);
      req.setIdentificationId(data.identificationId);
      req.setIdentificationType(data.identificationType);
      req.setIdentificationValue(data.identificationValue);
      req.setAddressId(data.addressId);
      req.setAddressType(data.addressType);
      req.setAddress1(data.address1);
      req.setAddress2(data.address2);
      req.setCity(data.city);
      req.setAddressState(data.addressState);
      req.setZip(data.zip?.replaceAll('_',''));
      req.setCountry(data.country);
    }

    service.saveTrustedContact(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function saveRegistrationEmployment(data) {
  return new Promise((resolve, reject) => {
    const req = new RegistrationEmployment();

    req.setOwnerId(data.ownerId);
    req.setEmploymentStatus(data.employmentStatus);
    req.setEmploymentName(data.employmentName);
    req.setPreviousEmploymentName(data.previousEmploymentName);
    req.setYearsEmployed(data.yearsEmployed);
    req.setEmploymentOccupation(data.employmentOccupation);
    req.setNatureOfBusiness(data.natureOfBusiness);

    service.saveRegistrationEmployment(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function saveRegistrationAssociation(data) {
  return new Promise((resolve, reject) => {
    const req = new RegistrationAssociation();

    req.setOwnerId(data.ownerId);
    req.setOwnership(data.ownership);
    req.setStockOwnership(data.stockOwnership);
    req.setBrokerMember(data.brokerMember);
    req.setBrokerName(data.brokerName);
    req.setAffiliatePerson(data.affiliatePerson);
    req.setAffiliatePersonPosition(data.affiliatePersonPosition);

    service.saveRegistrationAssociation(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function saveRegistrationDisclosure(agreementType, isAccepted) {
  return new Promise((resolve, reject) => {
    let req = new SaveRegistrationDisclosureRequest();
    req.setType(agreementType);
    req.setIsAccepted(isAccepted);

    service.saveRegistrationDisclosure(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function saveRegistrationTrading(data) {
  return new Promise((resolve, reject) => {
    let req = new SaveRegistrationTradingRequest();
    req.setMarginType(data.marginType);
    req.setStocks(data.stocks);
    req.setOption(data.option);
    req.setPrivacyConsent(data.privacyConsent);
    req.setAccreditedInvestor(data.accreditedInvestor);
    req.setMarginAgreement(data.marginAgreement);
    req.setMarginDisclosure(data.marginDisclosure);

    service.saveRegistrationTrading(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function saveRegistrationObjective(data) {
  return new Promise((resolve, reject) => {
    let req = new RegistrationObjective();
    req.setInvestmentObjectiveCode(data.investmentObjectiveCode);
    req.setAnnualIncome(data.annualIncome);
    req.setLiquidNetWorth(data.liquidNetWorth);
    req.setSourceOfFunds(data.sourceOfFunds);
    req.setSourceOfFundsDescription(data.sourceOfFundsDescription);
    req.setRiskTolerance(data.riskTolerance);

    service.saveRegistrationObjective(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function signRegistration(data) {
  return new Promise((resolve, reject) => {
    let req = new SignRegistrationRequest();
    req.setImgSignature(data.imgSignature);

    service.signRegistration(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function getRegistrationAccount(accountId) {
  return new Promise((resolve, reject) => {
    let req = new GetRegistrationAccountRequest();
    req.setAccountId(accountId);

    service.getRegistrationAccount(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function saveRegistrationKyc(data) {
  return new Promise((resolve, reject) => {
    let req = new SaveRegistrationKycRequest();
    req.setOwnerId(data.ownerId);
    req.setKycStatus(data.kycStatus);
    req.setPrimaryOwner(newRegistrationIdentification(data));

    service.saveRegistrationKyc(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function getCorpAttachments(accountId) {
  return new Promise((resolve, reject) => {
    let req = new GetRegistrationAccountRequest();
    req.setAccountId(accountId);

    service.getCorpAttachments(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}
